import React, { useState, useEffect } from "react";
import {
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Badge,
  Chip,
} from "@mui/material";
import {
  PriceChangeOutlined,
  PriceCheckOutlined,
  PendingActionsOutlined,
  AddPhotoAlternateOutlined,
  PostAddOutlined,
  MarkEmailUnreadOutlined,
  ForwardToInboxOutlined,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { backendGet, backendPost } from "@accredo/fe-shared/services/dataService.js";
import CircleIcon from "@mui/icons-material/Circle";
import { useNavigate } from "react-router";
import { useSnackbar } from "notistack";
import { getDatumZeit } from "@accredo/fe-shared/services/formatService.js";
import { styled } from "@mui/material/styles";
import { colorsSemantic } from "@accredo/fe-shared/theme/index.js";

const MAX_RECENT_NOTIFICATIONS = 5;

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: colorsSemantic.icon.accent.gold,
    color: theme.palette.common.white,
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: 1.5,
    minWidth: "22px",
    height: "22px",
    padding: `0 ${theme.spacing(0.75)}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "999px",
  },
}));

export default function NotificationPopover({
  anchorEl,
  onClose,
  bellNotifications = [],
  setBellNotifications = () => {},
}) {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!anchorEl) {
        return; // Nur laden, wenn das Popover geöffnet ist
      }

      // Fetch only recent unread notifications for the popover
      await backendGet("data/notifications", {
        label: "Benachrichtigungen",
        setResult: (res) => {
          setNotifications(res.data || []);
          setTotalNotifications(res.meta?.total || 0);
        },
        enqueueSnackbar,
        options: {
          params: {
            include: ["ref"],
            limit: MAX_RECENT_NOTIFICATIONS,
            sort: "-created_at",
            filter: { seen: false },
          },
        },
      });
    };
    fetchNotifications();
  }, [anchorEl]);

  const handleNotificationClick = (notification) => {
    backendPost("data/notifications/" + notification.id + "/seen", {
      label: "Notification - gelesen",
      //setIsLoading: setIsLoading,
      enqueueSnackbar: enqueueSnackbar,
    }).then(() => {
      notification.seen_at = new Date();

      // Aktualisiere die Benachrichtigung in der Liste
      setBellNotifications(
        bellNotifications.map((n) =>
          n.id === notification.id ? { ...n, seen_at: new Date() } : n
        )
      );
    });

    if (notification.url) {
      navigate(notification.url);
    }
  };

  const getNotificationIcons = (notification) => {
    let icons = [];

    switch (notification.type) {
      case "damage_booking_diff":
        return <PriceChangeOutlined />;
        break;
      case "damage_booking_released":
        return <PriceCheckOutlined />;
        break;
      case "damage_reminder":
        return <PendingActionsOutlined />;

        break;
      case "new_damage_attachment":
        return <AddPhotoAlternateOutlined />;

        break;
      case "new_damage_form":
        return <PostAddOutlined />;

        break;
      case "unassigned_incoming_mails":
        return <ForwardToInboxOutlined />;
        break;
      case "new_communication":
      case "incoming_mail_assigned":
      default:
        return <MarkEmailUnreadOutlined />;
        break;
    }
  };

  const handleViewAllClick = () => {
    onClose();
    navigate("/notifications");
  };

  const open = Boolean(anchorEl);
  const id = open ? "notification-popover" : undefined;

  const unreadCount = totalNotifications;
  const hasMoreNotifications = unreadCount > MAX_RECENT_NOTIFICATIONS;

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      <Box sx={{ width: 380 }}>
        <Box sx={{ p: 4, pb: 1 }}>
          <Typography variant="h6">
            Neue Benachrichtigungen
            {unreadCount > 0 && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                ({unreadCount})
              </Typography>

              //   <StyledBadge
              //   badgeContent={unreadCount}
              // invisible={unreadCount === 0}
              // />
            )}
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Lade Benachrichtigungen...
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ py: 0, paddingX: 4 }}>
              {notifications.length > 0 ? (
                notifications.map((notification, index) => (
                  <ListItem
                    key={notification.id || index}
                    sx={{
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      cursor: notification.url ? "pointer" : "default",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                      paddingX: 0,
                    }}
                    onClick={() => {
                      handleNotificationClick(notification);
                      onClose();
                    }}
                  >
                    <ListItemText
                      primary={
                        <>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                          >
                            {getDatumZeit(notification.created_at)}
                          </Typography>

                          <Typography variant="subtitle2" fontWeight="bold">
                            {notification.title}
                          </Typography>
                        </>
                      }
                      secondary={
                        <>
                          {notification.substituted_user_id &&
                            notification.substitution_expired && (
                              <Chip
                                label="Stellvertretung abgelaufen"
                                size="small"
                                color="warning"
                                sx={{ mb: 0.5 }}
                              />
                            )}
                          {/* pre-line: some notifications (e.g. pending bank
                              account lists/digests) join multiple entries with
                              "\n" — without this the browser collapses them
                              into one unreadable line. */}
                          <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                            {notification.description}
                          </Box>
                          {notification.type === "schaden" &&
                            notification.ref && (
                              <Box sx={{ mt: 1 }}>
                                <Box>
                                  <Box
                                    sx={{
                                      display: "inline-block",
                                      backgroundColor: "white",
                                      border: "1px solid #ccc",
                                      color: "black",
                                      padding: "2px 8px",
                                      borderRadius: "4px",
                                      fontSize: "0.75rem",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {notification.ref.schaden_nummer_accredo}
                                  </Box>
                                </Box>

                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  {notification.ref.objekt.bezeichnung}
                                </Typography>
                              </Box>
                            )}
                        </>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="body2" color="textSecondary">
                    Keine neuen Benachrichtigungen
                  </Typography>
                </Box>
              )}
            </List>

            <Box sx={{ p: 1.5, display: "flex", justifyContent: "center" }}>
              <Button
                onClick={handleViewAllClick}
                endIcon={<ChevronRightIcon />}
                sx={{ textTransform: "none" }}
              >
                Alle Benachrichtigungen anzeigen
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Popover>
  );
}
